import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link, useNavigate, useLocation } from 'react-router-dom';
import api from './api';
import DealDetail from './pages/DealDetail';
import Settings from './pages/Settings';
import Organization from './pages/Organization';
import CRM from './pages/CRM';
import Login from './pages/Login';
import GmailCallback from './pages/GmailCallback';
import Talent from './pages/Talent';
import TalentDetail from './pages/TalentDetail';
import TalentLists from './pages/TalentLists';
import TalentListDetail from './pages/TalentListDetail';
import TalentMetrics from './pages/TalentMetrics';
import TalentPerformance from './pages/TalentPerformance';
import InviteAccept from './pages/InviteAccept';
import './index.css';

// ============================================================
// AUTH CONTEXT
// ============================================================
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      api.get('/auth/me')
        .then(res => setUser(res.data.user))
        .catch(() => localStorage.removeItem('token'))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    localStorage.setItem('token', res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const register = async (email, name, password) => {
    const res = await api.post('/auth/register', { email, name, password });
    localStorage.setItem('token', res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, register, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// ============================================================
// PROTECTED ROUTE
// ============================================================
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-nextide-turquoise">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  return children;
};


// ============================================================
// DESIGN TOKENS
// ============================================================
const DS = {
  bg: { root: '#09090b', surface: '#111113', raised: '#18181b', hover: '#1f1f23' },
  border: { subtle: 'rgba(255,255,255,0.04)', default: 'rgba(255,255,255,0.07)', strong: 'rgba(255,255,255,0.12)', accent: 'rgba(30,228,188,0.25)' },
  text: { primary: '#f4f4f5', secondary: 'rgba(244,244,245,0.65)', muted: 'rgba(244,244,245,0.35)', ghost: 'rgba(244,244,245,0.2)' },
  accent: {
    green: '#1EE4BC', greenDim: 'rgba(30,228,188,0.10)',
    amber: '#FFDA53', amberDim: 'rgba(255,218,83,0.10)',
    red: '#FF3355', redDim: 'rgba(255,51,85,0.10)',
    violet: '#AF2EFF', violetDim: 'rgba(175,46,255,0.10)',
    cyan: '#1EE4BC',
  },
  radius: { sm: 6, md: 10, lg: 14 },
  font: {
    sans: "'Obviously', -apple-system, BlinkMacSystemFont, sans-serif",
    mono: "'JetBrains Mono', 'SF Mono', monospace",
  },
};


// ============================================================
// COMMAND PALETTE (Cmd+K / Ctrl+K)
// ============================================================
const CommandPalette = ({ open, onClose }) => {
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
      setQuery('');
    }
  }, [open]);

  const commands = [
    { label: 'CRM — Pipeline', section: 'Navigate', path: '/crm?tab=pipeline' },
    { label: 'CRM — Partners', section: 'Navigate', path: '/crm?tab=partners' },
    { label: 'Talent Network', section: 'Navigate', path: '/talent-metrics' },
    { label: 'Talent Lists', section: 'Navigate', path: '/talent-lists' },
    { label: 'Organization', section: 'Navigate', path: '/organization' },
    { label: 'Profile', section: 'Navigate', path: '/settings' },
  ];

  const filtered = commands.filter(c =>
    c.label.toLowerCase().includes(query.toLowerCase())
  );
  const sections = [...new Set(filtered.map(c => c.section))];

  if (!open) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        paddingTop: '18vh', animation: 'shellFadeIn 0.12s ease both',
        fontFamily: DS.font.sans,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: 500, background: DS.bg.surface,
          border: '1px solid ' + DS.border.strong,
          borderRadius: DS.radius.lg, overflow: 'hidden',
          boxShadow: '0 25px 60px rgba(0,0,0,0.5)',
        }}
      >
        <div style={{
          padding: '14px 16px', borderBottom: '1px solid ' + DS.border.default,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Escape') onClose();
              if (e.key === 'Enter' && filtered.length > 0) {
                navigate(filtered[0].path);
                onClose();
              }
            }}
            placeholder="Search commands, pages..."
            style={{
              flex: 1, background: 'none', border: 'none', outline: 'none',
              color: DS.text.primary, fontSize: 14, fontFamily: DS.font.sans,
            }}
          />
          <kbd style={{
            fontSize: 9, color: DS.text.ghost, background: DS.bg.raised,
            padding: '2px 6px', borderRadius: 3, border: '1px solid ' + DS.border.subtle,
          }}>ESC</kbd>
        </div>
        <div style={{ maxHeight: 320, overflow: 'auto', padding: 6 }}>
          {sections.map(section => (
            <div key={section}>
              <div style={{
                fontSize: 9, fontWeight: 600, color: DS.text.ghost,
                textTransform: 'uppercase', letterSpacing: '1.2px',
                padding: '10px 12px 4px',
              }}>{section}</div>
              {filtered.filter(c => c.section === section).map((cmd, i) => (
                <button
                  key={i}
                  onClick={() => { navigate(cmd.path); onClose(); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '8px 12px', borderRadius: DS.radius.sm,
                    border: 'none', cursor: 'pointer', width: '100%',
                    background: 'transparent', color: DS.text.secondary,
                    fontSize: 13, fontFamily: DS.font.sans, textAlign: 'left',
                    transition: 'background 0.1s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = DS.bg.hover; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                >
                  {cmd.label}
                </button>
              ))}
            </div>
          ))}
          {filtered.length === 0 && (
            <div style={{ padding: '20px 12px', textAlign: 'center', color: DS.text.ghost, fontSize: 13 }}>
              No results for "{query}"
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


// ============================================================
// LAYOUT — simplified sidebar (CRM, Talent) + Cmd+K
// ============================================================
const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [cmdOpen, setCmdOpen] = useState(false);

  // Command palette keyboard shortcut
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCmdOpen(prev => !prev);
      }
      if (e.key === 'Escape') setCmdOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Nav groups — filtered by user permissions
  const perms = user?.permissions || {};
  const isLegacyAdmin = !perms || Object.keys(perms).length === 0;
  const hasPerm = (key) => isLegacyAdmin || !!perms[key];

  const allNavGroups = [
    {
      label: 'PIPELINE',
      items: [
        hasPerm('pipeline') && { id: 'crm', path: '/crm', label: 'CRM' },
        (hasPerm('talent_network') || hasPerm('talent_lists') || hasPerm('talent_metrics')) && { id: 'talent', path: '/talent-metrics', label: 'Talent' },
      ].filter(Boolean),
    },
  ].filter(g => g.items.length > 0);
  const navGroups = allNavGroups;

  // Active nav detection
  const getActiveId = (pathname) => {
    if (pathname === '/crm' || pathname.startsWith('/crm') ||
        pathname.startsWith('/pipeline/')) return 'crm';
    if (pathname === '/talent' || pathname.startsWith('/talent/') ||
        pathname === '/talent-lists' || pathname.startsWith('/talent-lists/') ||
        pathname === '/talent-metrics' || pathname === '/talent-performance') return 'talent';
    return null;
  };

  const activeId = getActiveId(location.pathname);

  const initials = user?.name
    ? user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : '?';

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: DS.bg.root }}>
      {/* COMMAND PALETTE */}
      <CommandPalette open={cmdOpen} onClose={() => setCmdOpen(false)} />

      {/* SIDEBAR */}
      <nav style={{
        width: 216, minWidth: 216, height: '100vh', position: 'sticky', top: 0,
        background: DS.bg.root,
        borderRight: '1px solid ' + DS.border.subtle,
        display: 'flex', flexDirection: 'column',
        fontFamily: DS.font.sans,
        zIndex: 100,
      }}>
        {/* Brand */}
        <div style={{ padding: '20px 18px 24px', display: 'flex', alignItems: 'center', gap: 10 }}>
          <img src="/nextide-mark.svg" alt="NexTide" style={{ width: 28, height: 28 }} />
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: DS.text.primary, letterSpacing: '-0.4px', lineHeight: 1 }}>
              Automata
            </div>
            <div style={{ fontSize: 9, color: DS.accent.green, textTransform: 'uppercase', letterSpacing: '1.8px', marginTop: 2 }}>
              NexTide
            </div>
          </div>
        </div>

        {/* Nav groups */}
        <div style={{ flex: 1, padding: '0 8px', overflow: 'auto' }}>
          {navGroups.map(group => (
            <div key={group.label} style={{ marginBottom: 20 }}>
              <div style={{
                fontSize: 9, fontWeight: 600, textTransform: 'uppercase',
                letterSpacing: '1.8px', color: DS.text.ghost,
                padding: '0 10px', marginBottom: 4,
              }}>{group.label}</div>
              {group.items.map(item => {
                const isActive = activeId === item.id;
                return (
                  <Link
                    key={item.id}
                    to={item.path}
                    style={{
                      display: 'flex', alignItems: 'center',
                      padding: '8px 10px', borderRadius: DS.radius.sm,
                      textDecoration: 'none',
                      background: isActive ? 'rgba(30,228,188,0.07)' : 'transparent',
                      color: isActive ? DS.accent.green : DS.text.muted,
                      fontSize: 13, fontWeight: isActive ? 600 : 400,
                      fontFamily: DS.font.sans,
                      transition: 'all 0.12s',
                      position: 'relative',
                    }}
                    onMouseEnter={e => {
                      if (!isActive) {
                        e.currentTarget.style.background = DS.bg.hover;
                        e.currentTarget.style.color = DS.text.secondary;
                      }
                    }}
                    onMouseLeave={e => {
                      if (!isActive) {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.color = DS.text.muted;
                      }
                    }}
                  >
                    {isActive && (
                      <div style={{
                        position: 'absolute', left: 0, top: '50%',
                        transform: 'translateY(-50%)',
                        width: 3, height: 16, borderRadius: 2,
                        background: DS.accent.green,
                      }} />
                    )}
                    <span style={{ flex: 1, paddingLeft: 6 }}>{item.label}</span>
                  </Link>
                );
              })}
            </div>
          ))}
        </div>

        {/* Organization + Profile */}
        <div style={{ padding: '0 8px 4px' }}>
          {user?.org_id && (
            <Link
              to="/organization"
              style={{
                display: 'flex', alignItems: 'center',
                padding: '8px 16px', borderRadius: DS.radius.sm,
                textDecoration: 'none',
                background: location.pathname === '/organization' ? 'rgba(30,228,188,0.07)' : 'transparent',
                color: location.pathname === '/organization' ? DS.accent.green : DS.text.muted,
                fontSize: 13, fontWeight: location.pathname === '/organization' ? 600 : 400,
                fontFamily: DS.font.sans,
                transition: 'all 0.12s',
              }}
              onMouseEnter={e => {
                if (location.pathname !== '/organization') {
                  e.currentTarget.style.background = DS.bg.hover;
                  e.currentTarget.style.color = DS.text.secondary;
                }
              }}
              onMouseLeave={e => {
                if (location.pathname !== '/organization') {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = DS.text.muted;
                }
              }}
            >
              Organization
            </Link>
          )}
          <Link
            to="/settings"
            style={{
              display: 'flex', alignItems: 'center',
              padding: '8px 16px', borderRadius: DS.radius.sm,
              textDecoration: 'none',
              background: location.pathname === '/settings' ? 'rgba(30,228,188,0.07)' : 'transparent',
              color: location.pathname === '/settings' ? DS.accent.green : DS.text.muted,
              fontSize: 13, fontWeight: location.pathname === '/settings' ? 600 : 400,
              fontFamily: DS.font.sans,
              transition: 'all 0.12s',
            }}
            onMouseEnter={e => {
              if (location.pathname !== '/settings') {
                e.currentTarget.style.background = DS.bg.hover;
                e.currentTarget.style.color = DS.text.secondary;
              }
            }}
            onMouseLeave={e => {
              if (location.pathname !== '/settings') {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = DS.text.muted;
              }
            }}
          >
            Profile
          </Link>
        </div>

        {/* User */}
        <div style={{
          padding: '10px 12px 16px',
          borderTop: '1px solid ' + DS.border.subtle,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <div style={{
            width: 30, height: 30, borderRadius: 8,
            background: DS.accent.violet + '15', color: DS.accent.violet,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 11, fontWeight: 700, flexShrink: 0,
            border: '1px solid ' + DS.accent.violet + '20',
            letterSpacing: '-0.5px',
          }}>
            {initials}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: DS.text.primary }}>{user?.name}</div>
            <div style={{
              fontSize: 9, color: DS.text.ghost,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{user?.email}</div>
          </div>
          <button
            onClick={logout}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: DS.text.ghost, fontSize: 11, fontFamily: DS.font.sans,
              padding: '4px 8px', borderRadius: 4,
              transition: 'color 0.12s',
            }}
            onMouseEnter={e => { e.currentTarget.style.color = DS.text.secondary; }}
            onMouseLeave={e => { e.currentTarget.style.color = DS.text.ghost; }}
          >
            Logout
          </button>
        </div>
      </nav>

      {/* MAIN CONTENT */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: '100vh', minWidth: 0, overflow: 'hidden' }}>
        {/* Top bar with Cmd+K */}
        <div style={{
          height: 42, minHeight: 42,
          borderBottom: '1px solid ' + DS.border.subtle,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '0 16px',
          background: DS.bg.root,
          fontFamily: DS.font.sans,
          position: 'sticky', top: 0, zIndex: 50,
        }}>
          <button
            onClick={() => setCmdOpen(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '5px 12px', borderRadius: DS.radius.sm,
              border: '1px solid ' + DS.border.default,
              background: 'transparent', color: DS.text.ghost,
              fontSize: 12, cursor: 'pointer', fontFamily: DS.font.sans,
              transition: 'border-color 0.12s',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = DS.border.strong; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = DS.border.default; }}
          >
            Search or command...
            <kbd style={{
              fontSize: 9, background: DS.bg.raised, padding: '1px 5px',
              borderRadius: 3, border: '1px solid ' + DS.border.subtle,
              color: DS.text.ghost,
            }}>
              {typeof navigator !== 'undefined' && navigator.platform && navigator.platform.includes('Mac') ? '\u2318K' : 'Ctrl+K'}
            </kbd>
          </button>
        </div>

        {/* Page content */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden" style={{ minWidth: 0 }}>
          {children}
        </div>
      </div>
    </div>
  );
};


// ============================================================
// APP
// ============================================================
function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/invite/:token" element={<InviteAccept />} />
          <Route path="/auth/gmail/callback" element={<GmailCallback />} />

          {/* Protected routes */}
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <Layout>
                  <Routes>
                    {/* CRM hub */}
                    <Route path="/crm" element={<CRM />} />
                    <Route path="/pipeline/:id" element={<DealDetail />} />

                    {/* Organization */}
                    <Route path="/organization" element={<Organization />} />

                    {/* Profile */}
                    <Route path="/settings" element={<Settings />} />

                    {/* Talent */}
                    <Route path="/talent" element={<Talent />} />
                    <Route path="/talent/:id" element={<TalentDetail />} />
                    <Route path="/talent-lists" element={<TalentLists />} />
                    <Route path="/talent-lists/:id" element={<TalentListDetail />} />
                    <Route path="/talent-metrics" element={<TalentMetrics />} />
                    <Route path="/talent-performance" element={<TalentPerformance />} />

                    {/* Default + legacy redirects */}
                    <Route path="/" element={<Navigate to="/crm" replace />} />
                    <Route path="/mission" element={<Navigate to="/crm" replace />} />
                    <Route path="/dashboard" element={<Navigate to="/crm" replace />} />
                    <Route path="/outreach" element={<Navigate to="/crm" replace />} />
                    <Route path="/prospector" element={<Navigate to="/crm" replace />} />
                    <Route path="/brain" element={<Navigate to="/crm" replace />} />
                    <Route path="/contacts" element={<Navigate to="/crm" replace />} />
                    <Route path="/contacts/*" element={<Navigate to="/crm" replace />} />
                    <Route path="/sequences" element={<Navigate to="/crm" replace />} />
                    <Route path="/research" element={<Navigate to="/crm" replace />} />
                    <Route path="/campaigns" element={<Navigate to="/crm" replace />} />
                    <Route path="/campaigns/*" element={<Navigate to="/crm" replace />} />
                    <Route path="/inbox" element={<Navigate to="/crm" replace />} />
                    <Route path="/meetings" element={<Navigate to="/crm" replace />} />
                    <Route path="/companies" element={<Navigate to="/crm" replace />} />
                    <Route path="/companies/*" element={<Navigate to="/crm" replace />} />
                    <Route path="/pipeline" element={<Navigate to="/crm?tab=pipeline" replace />} />

                    {/* Catch-all */}
                    <Route path="*" element={<Navigate to="/crm" replace />} />
                  </Routes>
                </Layout>
              </ProtectedRoute>
            }
          />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
